import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// ─── ResdentialImageView ─────────────────────────────────────────────────────
// variant="blueprint" → light green banner header, image area fills remainder
// variant="3d"        → gray pill title, image area fills remainder
// Toolbar is always overlaid in the TOP-RIGHT corner of the image area.
// Fullscreen toggles between enter/exit with the correct icon.
// Zoom is always centered (transformOrigin: center center).
export default function ResdentialImageView({
  image,
  viewerId = "residential-viewer",
  variant = "3d",
  title,
}) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragStart = useRef(null);

  const isVideo = (url) => {
    if (typeof url !== 'string') return false;
    return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
  };

  // ── Zoom (always from center) ──
  const handleZoomIn = () => setScale((s) => Math.min(s + 0.25, 5));
  const handleZoomOut = () =>
    setScale((s) => {
      const next = Math.max(s - 0.25, 0.5);
      if (next <= 1) setPosition({ x: 0, y: 0 }); // snap back to center
      return next;
    });
  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // ── Fullscreen toggle (enter / exit) ──
  const handleFullscreen = () => {
    const el = document.getElementById(viewerId);
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.() ?? el?.webkitRequestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // ── Listen for browser fullscreen changes ──
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // ── Pan handlers (only active when zoomed in) ──
  const onMouseDown = (e) => {
    if (e.target.closest("button")) return; // don't drag on toolbar clicks
    if (scale <= 1) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };
  const onMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };
  const onMouseUp = () => setIsDragging(false);

  // ── Toolbar: absolutely positioned top-right inside the image area ──
  const toolbar = (
    <Box
      onMouseDown={(e) => e.stopPropagation()} // prevent starting a pan drag
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 0.2,
        bgcolor: "rgba(255,255,255,0.90)",
        borderRadius: "10px",
        px: 0.5,
        py: 0.3,
        boxShadow: "0 2px 10px rgba(0,0,0,0.14)",
        backdropFilter: "blur(6px)",
      }}
    >
      {/* Reset — only when zoomed */}
      {scale !== 1 && (
        <Tooltip title="Reset" arrow>
          <IconButton
            size="small"
            onClick={handleReset}
            sx={{
              color: "#4a8f3c",
              "&:hover": { bgcolor: "rgba(74,143,60,0.10)" },
            }}
          >
            <RestartAltIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Zoom In */}
      <Tooltip title="Zoom In" arrow>
        <IconButton
          size="small"
          onClick={handleZoomIn}
          sx={{ color: "#555", "&:hover": { color: "#4a8f3c", bgcolor: "transparent" } }}
        >
          <ZoomInIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {/* Zoom Out */}
      <Tooltip title="Zoom Out" arrow>
        <IconButton
          size="small"
          onClick={handleZoomOut}
          sx={{ color: "#555", "&:hover": { color: "#4a8f3c", bgcolor: "transparent" } }}
        >
          <ZoomOutIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {/* Fullscreen / Exit Fullscreen */}
      <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} arrow>
        <IconButton
          size="small"
          onClick={handleFullscreen}
          sx={{ color: "#555", "&:hover": { color: "#4a8f3c", bgcolor: "transparent" } }}
        >
          {isFullscreen ? (
            <FullscreenExitIcon sx={{ fontSize: 20 }} />
          ) : (
            <FullscreenIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ── Shared image area (toolbar sits inside, absolutely top-right) ──
  const imgArea = (
    <Box
      sx={{
        flex: 1,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#fafafa",
        cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
        userSelect: "none",
        // Fullscreen: ensure the image area fills the screen
        ...(isFullscreen && {
          width: "100%",
          height: "100%",
          flex: "unset",
        }),
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {/* TOP-RIGHT TOOLBAR */}
      {toolbar}

      {/* MEDIA AREA — zooms from center */}
      {isVideo(image) ? (
        <Box
          component="video"
          src={image}
          controls
          autoPlay
          loop
          muted
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging ? "none" : "transform 0.22s ease",
            transformOrigin: "center center",
          }}
        />
      ) : (
        <Box
          component="img"
          src={image}
          alt="plot view"
          draggable={false}
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging ? "none" : "transform 0.22s ease",
            transformOrigin: "center center",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Zoom % badge — bottom left */}
      {scale !== 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 10,
            bgcolor: "rgba(0,0,0,0.42)",
            color: "#fff",
            borderRadius: "6px",
            px: 1,
            py: 0.2,
            fontSize: 11,
            fontWeight: 700,
            pointerEvents: "none",
          }}
        >
          {Math.round(scale * 100)}%
        </Box>
      )}
    </Box>
  );

  /* ══════════════════════════════════════════════════════════
     VARIANT: blueprint
     • Light green full-width banner at top
     • Image area fills the rest (toolbar overlaid top-right)
  ══════════════════════════════════════════════════════════ */
  if (variant === "blueprint") {
    return (
      <Box
        id={viewerId}
        sx={{
          borderRadius: "16px",
          boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
          border: "1px solid #e8e8e8",
          bgcolor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
        }}
      >
        {/* GREEN BANNER */}
        <Box
          sx={{
            bgcolor: "#eef5dc",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={16}
            sx={{ color: "#3a6e00", letterSpacing: 0.1 }}
          >
            {title || "GreenHeap Plots Blueprint"}
          </Typography>
        </Box>

        {/* IMAGE AREA (toolbar is inside) */}
        {imgArea}
      </Box>
    );
  }

  /* ══════════════════════════════════════════════════════════
     VARIANT: 3d (default)
     • Gray pill title centered at top
     • Image area fills the rest (toolbar overlaid top-right)
  ══════════════════════════════════════════════════════════ */
  return (
    <Box
      id={viewerId}
      sx={{
        borderRadius: "16px",
        boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
        border: "1px solid #e8e8e8",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
      }}
    >
      {/* GRAY PILL TITLE */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: 1.2,
          pb: 0.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{ bgcolor: "#f0f0f0", borderRadius: "20px", px: 3, py: 0.5 }}
        >
          <Typography
            fontWeight={600}
            fontSize={13}
            sx={{ color: "#4a8f3c", letterSpacing: 0.2 }}
          >
            {title || "3D Image"}
          </Typography>
        </Box>
      </Box>

      {/* IMAGE AREA (toolbar is inside) */}
      {imgArea}
    </Box>
  );
}
